import {
  ALERT_CREATOR_ACCESS_LEVELS,
  FEMA_PERMISSIONS,
} from 'features/Weather/features/WeatherAlerts/features/AlertCreator/components/IpawsAlertFields/config/Ipaws.config';

/**
 * The permission that each builder action category needs.
 *
 * This map MIRRORS the backend map `actionPermissions` in
 * `Fusion-Backend/src/features/GlobalAction/config/GlobalAction.config.ts` (the Global Action
 * permission middleware, PR #1482). The backend refuses a create, an update or an enable when the
 * user does not have the key for each action. Change the two maps together.
 *
 * The backend function names map to these builder categories: `ipaws` is 'Publish IPAWS Alert',
 * `deviceaction` is 'Device Action', `devicegroupaction` is 'Device Group Action'.
 *
 * `strict` mirrors the backend rule: a strict key has no admin bypass (the FEMA keys; also
 * `STRICT_PERMISSIONS` in `PermsContext`). A non-strict key passes for the admin levels that the
 * backend bypasses (`ADMIN_BYPASS_ACCESS_LEVELS`), whether or not the role holds the key.
 */

/** The rule of one category: the permission key, the access levels that `testPermission` reads, and the strictness. */
export type ActionCategoryPermissionRule = {
  permission: string;
  levels: number[];
  strict: boolean;
};

/**
 * The access levels that the backend lets through a NON-STRICT key without the key
 * (`isAdminAccessLevel` in `Fusion-Backend/src/features/Middleware/PermissionMiddleware.ts`).
 * `PermsContext.testPermission` bypasses only the super user (8), so the builder applies this list
 * itself for the non-strict rules.
 */
export const ADMIN_BYPASS_ACCESS_LEVELS: readonly number[] = [8, 11, 12, 13];

/** The code of the backend 403 answer to a create, an update or an enable. */
export const GLOBAL_ACTION_PERMISSION_CODE = 'GLOBAL_ACTION_PERMISSION_REQUIRED';

/** The builder action category to its permission rule. A category that is not here has no rule. */
export const ACTION_CATEGORY_PERMISSIONS: Readonly<Record<string, ActionCategoryPermissionRule>> = {
  'Publish IPAWS Alert': {
    permission: FEMA_PERMISSIONS.create,
    levels: ALERT_CREATOR_ACCESS_LEVELS,
    strict: true,
  },
  'Device Action': { permission: 'SendDeviceCommands', levels: [], strict: false },
  'Device Group Action': { permission: 'SendDeviceGroupCommands', levels: [], strict: false },
};
